import { useClerk, useUser } from "@clerk/clerk-react";
import { useQueryClient } from "@tanstack/react-query";
import { Camera, Loader2, LogOut } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { DESK_PROFILE_KEY, useDeskProfile } from "@/hooks/useDeskProfile";
import { api } from "@/services/api";
import type { DeskProfileUpdate } from "@/types";

const MAX_PHOTO_MB = 5;

const JOURNEY = ["Profile", "Your report · brief from Jonty", "Feedback & score", "Your review"];

type Field = keyof DeskProfileUpdate;
type Errors = Partial<Record<Field | "photo", string>>;

const FIELDS: { name: Field; label: string; required: string; placeholder?: string }[] = [
  { name: "first_name", label: "First name", required: "Enter your first name" },
  { name: "last_name", label: "Last name", required: "Enter your last name" },
  { name: "role", label: "Role", required: "Enter your role", placeholder: "e.g. Credit Analyst" },
  { name: "firm", label: "Firm", required: "Enter your firm" },
  {
    name: "linkedin_url",
    label: "LinkedIn profile URL",
    required: "Enter your LinkedIn URL",
    placeholder: "https://www.linkedin.com/in/…",
  },
];

/** Mirrors the API check: an http(s) URL on linkedin.com or a subdomain of it. */
export function isLinkedinUrl(value: string): boolean {
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();
    return (
      (url.protocol === "https:" || url.protocol === "http:") &&
      (host === "linkedin.com" || host.endsWith(".linkedin.com"))
    );
  } catch {
    return false;
  }
}

function validate(values: DeskProfileUpdate, hasPhoto: boolean): Errors {
  const errors: Errors = {};
  if (!hasPhoto) errors.photo = "Upload a photo";
  for (const field of FIELDS) {
    if (!values[field.name].trim()) errors[field.name] = field.required;
  }
  if (!errors.linkedin_url && !isLinkedinUrl(values.linkedin_url.trim())) {
    errors.linkedin_url = "Must be a linkedin.com URL";
  }
  return errors;
}

/**
 * The profile a Desk Participant completes before reaching the dashboard (KC-507).
 * Saves into their existing profile names and the Desk enrolment's portfolio.
 */
export default function DeskProfileSetupPage() {
  const { data: profile, isPending } = useDeskProfile();
  const { user: clerkUser } = useUser();
  const { signOut } = useClerk();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const photoInputRef = useRef<HTMLInputElement>(null);

  const [values, setValues] = useState<DeskProfileUpdate>({
    first_name: "",
    last_name: "",
    role: "",
    firm: "",
    linkedin_url: "",
  });
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Seed the form once from what is already on file.
  const seeded = useRef(false);
  useEffect(() => {
    if (!profile || seeded.current) return;
    seeded.current = true;
    setValues({
      first_name: profile.first_name ?? "",
      last_name: profile.last_name ?? "",
      role: profile.role ?? "",
      firm: profile.firm ?? "",
      linkedin_url: profile.linkedin_url ?? "",
    });
    setPhotoUrl(profile.photo_url);
  }, [profile]);

  if (isPending) return null;
  // Only a Desk Participant has a profile to set up here.
  if (!profile?.programme_id) return <Navigate to="/" replace />;
  const programmeId = profile.programme_id;

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_PHOTO_MB * 1024 * 1024) {
      setErrors((current) => ({ ...current, photo: `Photo exceeds ${MAX_PHOTO_MB} MB limit` }));
      return;
    }
    setIsUploading(true);
    try {
      const portfolio = await api.uploadMyPortfolioPhoto(programmeId, file);
      setPhotoUrl(portfolio.photo_url);
      setErrors((current) => ({ ...current, photo: undefined }));
    } catch (err) {
      setErrors((current) => ({
        ...current,
        photo: err instanceof Error ? err.message : "Failed to upload photo",
      }));
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const found = validate(values, Boolean(photoUrl));
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    const payload: DeskProfileUpdate = {
      first_name: values.first_name.trim(),
      last_name: values.last_name.trim(),
      role: values.role.trim(),
      firm: values.firm.trim(),
      linkedin_url: values.linkedin_url.trim(),
    };
    setIsSaving(true);
    try {
      // Same write order as the other name forms: Clerk, then our profile.
      await clerkUser?.update({ firstName: payload.first_name, lastName: payload.last_name });
      const saved = await api.saveDeskProfile(payload);
      queryClient.setQueryData(DESK_PROFILE_KEY, saved);
      navigate("/", { replace: true });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to save your profile");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden bg-[#013220] text-[#F2EFE3]">
      <button
        type="button"
        onClick={() => signOut()}
        className="absolute right-4 top-4 flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] font-semibold text-[#8fa79a] transition-colors hover:text-[#F2EFE3] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2AB673] md:right-6 md:top-6"
      >
        <LogOut className="h-3.5 w-3.5" aria-hidden />
        Log out
      </button>

      <div className="mx-auto flex min-h-screen w-full max-w-[996px] flex-col justify-center gap-10 px-4 pb-12 pt-16 md:flex-row md:items-center md:gap-14 md:px-0 md:py-16">
        <section className="w-full md:w-[380px] md:flex-none">
          <p className="text-[13px] font-bold uppercase tracking-[3px] text-[#c2986a]">
            The Desk · By invitation
          </p>
          <h1 className="mt-4 [font-family:inherit] text-[32px] font-extrabold leading-[1.1] md:text-[40px]">
            Write one report.
            <br />
            Tell us what you think.
          </h1>
          <p className="mt-5 text-[16px] leading-[1.6] text-[#b6c3b8]">
            You’ve been invited to try the Dominion Programme as a finance professional. Set up your
            profile, submit one report, get our feedback — then share your honest review.
          </p>

          <ol aria-label="Your journey" className="mt-8 flex flex-col gap-2.5 text-[15px]">
            {JOURNEY.map((step, index) => (
              <li
                key={step}
                aria-current={index === 0 ? "step" : undefined}
                className={
                  index === 0
                    ? "flex items-center gap-3 font-bold text-[#F2EFE3]"
                    : "flex items-center gap-3 text-[#8fa79a]"
                }
              >
                <span
                  className={
                    index === 0
                      ? "flex h-7 w-7 flex-none items-center justify-center rounded-full bg-[#2AB673] text-[13px] font-extrabold text-[#012019]"
                      : "flex h-7 w-7 flex-none items-center justify-center rounded-full border border-[#3a5347] text-[13px] font-bold"
                  }
                >
                  {index + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>
        </section>

        <form
          onSubmit={handleSubmit}
          noValidate
          aria-labelledby="desk-profile-title"
          className="flex w-full flex-col gap-[18px] rounded-[20px] border border-[#124a37] bg-[#0b3a2b] p-6 md:w-[560px] md:flex-none md:p-9"
        >
          <div>
            <h2 id="desk-profile-title" className="[font-family:inherit] text-[22px] font-bold">
              Your profile
            </h2>
            <p className="mt-1 text-[14px] text-[#b6c3b8]">
              Shown on your review card if it’s published.
            </p>
          </div>

          {submitError && (
            <div
              role="alert"
              className="rounded-[10px] border border-[#f7a8a0]/40 bg-[#f7a8a0]/10 px-4 py-2.5 text-[14px] text-[#f7a8a0]"
            >
              {submitError}
            </div>
          )}

          <div>
            <div className="flex items-center gap-5">
              <div className="flex h-[88px] w-[88px] flex-none items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-[#2AB673] bg-[#124a37]">
                {photoUrl ? (
                  <img src={photoUrl} alt="Your photo" className="h-full w-full object-cover" />
                ) : (
                  <Camera className="h-7 w-7 text-[#7BDBAA]" aria-hidden />
                )}
              </div>
              <div className="min-w-0">
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={isUploading}
                  className="flex min-h-[44px] items-center gap-2 rounded-[10px] border border-[#2AB673] px-4 text-[14px] font-semibold text-[#7BDBAA] transition-colors hover:bg-[#2AB673]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2AB673] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0b3a2b] disabled:opacity-60"
                >
                  {isUploading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
                  {photoUrl ? "Replace photo" : "Upload photo"}
                </button>
                <p className="mt-2 text-[13px] text-[#8fa79a]">
                  Required · square, at least 400×400
                </p>
              </div>
              <label htmlFor="desk-photo" className="sr-only">
                Photo
              </label>
              <input
                id="desk-photo"
                ref={photoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handlePhotoChange}
              />
            </div>
            {errors.photo && <p className="mt-2 text-[13px] text-[#f7a8a0]">{errors.photo}</p>}
          </div>

          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
            {FIELDS.map((field) => (
              <div
                key={field.name}
                className={field.name === "linkedin_url" ? "sm:col-span-2" : undefined}
              >
                <label
                  htmlFor={`desk-${field.name}`}
                  className="mb-1.5 block text-[13px] font-semibold text-[#b6c3b8]"
                >
                  {field.label}
                </label>
                <input
                  id={`desk-${field.name}`}
                  className="block min-h-[44px] w-full rounded-[10px] border border-[#3a5347] bg-[#012019] px-3.5 text-[15px] font-medium text-[#F2EFE3] placeholder:text-[#8fa79a] focus-visible:border-[#2AB673] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2AB673]/60 aria-[invalid=true]:border-[#f7a8a0]"
                  type={field.name === "linkedin_url" ? "url" : "text"}
                  placeholder={field.placeholder}
                  value={values[field.name]}
                  aria-invalid={Boolean(errors[field.name])}
                  aria-describedby={errors[field.name] ? `desk-${field.name}-error` : undefined}
                  onChange={(e) =>
                    setValues((current) => ({ ...current, [field.name]: e.target.value }))
                  }
                />
                {errors[field.name] && (
                  <p id={`desk-${field.name}-error`} className="mt-1.5 text-[13px] text-[#f7a8a0]">
                    {errors[field.name]}
                  </p>
                )}
              </div>
            ))}
          </div>

          <button
            type="submit"
            disabled={isSaving || isUploading}
            className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-[10px] bg-[#2AB673] text-[15px] font-extrabold text-[#012019] transition-colors hover:bg-[#34c47f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7BDBAA] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0b3a2b] disabled:opacity-50"
          >
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            Save and see my brief
          </button>

          <p className="text-center text-[12px] text-[#8fa79a]">
            Nothing is public until you consent and we approve your review.
          </p>
        </form>
      </div>
    </div>
  );
}
